import type { FC } from 'react'
import { useState, useEffect } from 'react'
import styled from '@emotion/styled'

import type { AnimationItem } from 'lottie-web'

import Media from '@segment/matcha/components/Media'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useIsInView from '@segment/utils/useInView'
import useLottie from '@segment/utils/useLottie/useLottie'

import jsonUrl from './assets/personalization-takes-lottie.json.data'

const data = {
  title: `Personalization takes on new urgency`,
  descriptions: [
    `2020 transformed consumer behavior like never before. With stay-at-home orders in place and brick-and-mortar businesses closed, companies of all sizes were forced to reconsider their customer engagement strategies. However, this “flight to digital” radically increased consumers' expectations far faster than what many brands were ready for.`,
    `Overnight, consumers expected brands to remember who they were, where they came from, and what they liked, regardless of what channel they came from.`,
    `This promise of personalized, one-to-one marketing might once have been a pipe dream. It is now increasingly table stakes for consumers today. But as consumers’ expectations rise, are businesses equipped to deliver these experiences?`,
  ],
}

const PersonalizationTakes: FC = () => {
  const [animationData, setAnimationData] = useState<Promise<Response>>()
  useEffect(() => {
    setAnimationData(fetch(jsonUrl))
  }, [])
  const [animation, containerRef] = useLottie<AnimationItem, HTMLDivElement>({
    options: {
      autoplay: false,
      animationData,
      loop: false,
    },
  })
  const isInView = useIsInView(containerRef)

  useEffect(() => {
    if (isInView) {
      animation?.play()
    }
  }, [animation, isInView])

  return (
    <SectionBase reverse={true} variant="2Col">
      <Section.Contents>
        <Media greaterThan="md">
          <Animation ref={containerRef} />
        </Media>
      </Section.Contents>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading tag="h2" variant="h2">
            {data.title}
          </CopyText.Heading>
          {data.descriptions.map((description, key) => (
            <CopyText.Description key={key} tag="p" variant="pLarge">
              {description}
            </CopyText.Description>
          ))}
        </CopyTextBase>
      </Section.Copy>
    </SectionBase>
  )
}

const Animation = styled.div`
  max-width: 400px;
  margin: 0 auto;
  aspect-ratio: 338 / 585;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin: 0 30px 0 auto;
  }
`

export default PersonalizationTakes
